/**
 * Hook: useChats
 * Maneja lista de chats con caché, paginación, búsqueda server-side y auto-refresh
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import useCRMStore from '../store/useCRMStore'
import { chatsAPI } from '../services/api'

export const useChats = () => {
  const { chats, setChats, shouldFetchChats, activeFilters, sessionExpired } = useCRMStore()
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false
  })

  // Debounced search para búsqueda server-side
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const searchTimeoutRef = useRef(null)

  // Debounce del término de búsqueda (300ms)
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(activeFilters.search || '')
    }, 300)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [activeFilters.search])

  const loadChats = useCallback(async (append = false, silent = false, searchTerm = '') => {
    // No hacer requests si la sesión expiró
    if (sessionExpired) return

    try {
      // Solo mostrar loading si no es silent refresh
      if (!silent) {
        const currentLoading = append ? setLoadingMore : setLoading
        currentLoading(true)
      }
      setError(null)

      const offset = append ? pagination.offset + pagination.limit : 0
      const data = await chatsAPI.getChats(50, offset, searchTerm)

      // Actualizar paginación
      setPagination(data.pagination)

      // Actualizar chats (append o replace)
      const newChats = append ? [...chats, ...data.chats] : data.chats
      setChats(newChats)

    } catch (err) {
      if (!silent) {
        setError(err.message)
      }
      console.error('Error loading chats:', err)
    } finally {
      if (!silent) {
        setLoading(false)
        setLoadingMore(false)
      }
    }
  }, [pagination, chats, setChats, sessionExpired])

  // NOTA: Wassenger API no soporta obtener avatares de contactos
  // Los avatares se manejan con ui-avatars fallback en ChatItem.jsx (onError)

  const loadMoreChats = useCallback(() => {
    if (!loadingMore && pagination.hasMore) {
      loadChats(true, false, debouncedSearch)
    }
  }, [loadingMore, pagination.hasMore, loadChats, debouncedSearch])

  // Cargar chats iniciales al montar
  useEffect(() => {
    if (!chats || chats.length === 0) {
      loadChats(false, false, '')
    }
  }, [])

  // Recargar chats cuando cambia el término de búsqueda (debounced)
  useEffect(() => {
    // Solo recargar si hay un término de búsqueda o si estamos limpiando la búsqueda
    loadChats(false, false, debouncedSearch)
  }, [debouncedSearch])

  // ========================================
  // AUTO-REFRESH INTELIGENTE
  // ========================================
  // Estrategia: Detectar chats nuevos + actualizar contadores/timestamps
  const refreshChatUpdates = useCallback(async () => {
    // No hacer requests si la sesión expiró
    if (sessionExpired) return

    try {
      const data = await chatsAPI.getChatsUpdates()

      if (!chats || chats.length === 0) return

      // Crear mapa de chats actuales por teléfono
      const currentPhonesSet = new Set(chats.map(c => c.phone))

      // Detectar si hay chats nuevos en las actualizaciones
      const newChatPhones = data.updates.filter(u => !currentPhonesSet.has(u.phone))

      // Si hay chats nuevos, hacer reload completo para obtener toda la info
      if (newChatPhones.length > 0) {
        console.log(`Detectados ${newChatPhones.length} chat(s) nuevo(s), recargando lista...`)
        loadChats(false, true, debouncedSearch) // silent reload
        return
      }

      // Crear mapa de actualizaciones por teléfono
      const updatesMap = new Map(
        data.updates.map(update => [update.phone, update])
      )

      let hasChanges = false

      // Actualizar solo los campos modificados
      const updatedChats = chats.map(chat => {
        const update = updatesMap.get(chat.phone)
        if (!update) return chat

        // Solo actualizar si cambió el timestamp del último mensaje O el flag de la manito
        const manoChanged = update.isLastMessageFromClient !== undefined && update.isLastMessageFromClient !== chat.isLastMessageFromClient
        if (update.lastMessageTime !== chat.lastMessageTime || manoChanged) {
          hasChanges = true
          return {
            ...chat,
            lastMessage: update.lastMessage,
            lastMessageTime: update.lastMessageTime,
            unreadCount: update.unreadCount,
            isLastMessageFromClient: update.isLastMessageFromClient,
            last_from_client: update.isLastMessageFromClient
          }
        }

        return chat
      })

      // Si hubo cambios, reordenar por último mensaje (más reciente primero)
      if (hasChanges) {
        updatedChats.sort((a, b) => {
          const timeA = new Date(a.lastMessageTime || 0).getTime()
          const timeB = new Date(b.lastMessageTime || 0).getTime()
          return timeB - timeA
        })
        setChats(updatedChats)
      }
    } catch (err) {
      // Silenciar errores de refresh en background
      console.error('Error en auto-refresh:', err)
    }
  }, [chats, setChats, sessionExpired, loadChats, debouncedSearch])

  // Auto-refresh cada 5 segundos para mejor responsividad
  // Se detiene automaticamente si la sesion expira
  useEffect(() => {
    if (!chats || chats.length === 0 || sessionExpired) return

    const interval = setInterval(() => {
      // Doble check por si sessionExpired cambio entre intervalos
      if (!sessionExpired) {
        refreshChatUpdates()
      }
    }, 5000) // 5 segundos - más responsivo

    return () => clearInterval(interval)
  }, [chats, refreshChatUpdates, sessionExpired])

  // Filtrar chats según filtros activos (sobre todos los chats cargados)
  // NOTA: La búsqueda se hace en el servidor, aquí solo aplicamos filtros locales
  const filteredChats = (chats || []).filter(chat => {
    // Filtro de stage
    if (activeFilters.stage && chat.stage !== activeFilters.stage) {
      return false
    }

    // Filtro de agente
    if (activeFilters.agent) {
      if (activeFilters.agent === 'Sin Asignar') {
        if (chat.assignedTo && chat.assignedTo !== 'Ninguno') return false
      } else if (chat.assignedTo !== activeFilters.agent) {
        return false
      }
    }

    // Filtro de etiqueta (label)
    if (activeFilters.label) {
      const hasLabel = chat.labels?.some(l => l.id === activeFilters.label.id)
      if (!hasLabel) return false
    }

    // Filtro de departamento
    if (activeFilters.department) {
      if (chat.departmentId !== activeFilters.department.id) return false
    }

    return true
  })

  // Ordenar chats según filtro de orden
  const sortedChats = [...filteredChats].sort((a, b) => {
    const sortOrder = activeFilters.sortOrder || 'recent'

    switch (sortOrder) {
      case 'recent':
        // Más recientes primero (por último mensaje)
        const timeA = new Date(a.lastMessageTime || 0).getTime()
        const timeB = new Date(b.lastMessageTime || 0).getTime()
        return timeB - timeA

      case 'oldest':
        // Más antiguos primero
        const timeA2 = new Date(a.lastMessageTime || 0).getTime()
        const timeB2 = new Date(b.lastMessageTime || 0).getTime()
        return timeA2 - timeB2

      case 'name-asc':
        // Nombre A-Z
        return (a.name || '').localeCompare(b.name || '', 'es')

      case 'name-desc':
        // Nombre Z-A
        return (b.name || '').localeCompare(a.name || '', 'es')

      default:
        return 0
    }
  })

  return {
    chats: sortedChats,
    allChats: chats || [],
    loading,
    loadingMore,
    error,
    pagination,
    refresh: () => loadChats(false, false, debouncedSearch),
    loadMore: loadMoreChats
  }
}
